import { PhysioLayout } from "@/components/layout/physio-layout";
import { TrendingUp, CheckCircle } from "lucide-react";

const BANDS = [
  {
    band: "Band 5",
    title: "Newly Qualified / Foundation Physiotherapist",
    duration: "Typically 1–2 years",
    colour: "emerald",
    competencies: [
      "Develop core clinical skills in MSK and rheumatology assessment",
      "Manage a caseload of routine patients under supervision",
      "Begin to apply evidence-based practice in clinical decisions",
      "Engage with mandatory training and foundation CPD",
      "Build HCPC CPD log from day one",
    ],
    developmentFocus: [
      "Complete trust induction and mandatory competencies",
      "Seek a clinical supervisor and regular supervision",
      "Join the CSP and consider ACPR student/affiliate membership",
      "Attend departmental teaching and journal club",
      "Begin reading and applying NICE MSK and rheumatology guidelines",
    ],
    transition: "To progress to Band 6, demonstrate autonomous practice, growing caseload complexity, and initiation of a CPD portfolio.",
  },
  {
    band: "Band 6",
    title: "Specialist Physiotherapist",
    duration: "Typically 2–4 years",
    colour: "teal",
    competencies: [
      "Manage a complex, autonomous MSK / rheumatology caseload",
      "Lead patient group education or exercise classes",
      "Contribute to departmental audit and governance",
      "Supervise and educate Band 5s and students",
      "Develop specialist clinical skills (e.g. hydrotherapy, exercise prescription for axSpA)",
    ],
    developmentFocus: [
      "Obtain ACPR full membership",
      "Complete a postgraduate CPD module in rheumatology",
      "Begin building an APP pillar portfolio",
      "Attend CSP Congress and ACPR study days",
      "Develop skills in outcome measure administration (BASDAI, HAQ, DAS28)",
    ],
    transition: "To progress to Band 7, demonstrate advanced clinical reasoning, service leadership contribution, and education/supervision of others.",
  },
  {
    band: "Band 7",
    title: "Advanced / Senior Specialist Physiotherapist",
    duration: "Typically 3–5+ years",
    colour: "blue",
    competencies: [
      "Expert-level clinical reasoning and complex case management",
      "Lead service development and clinical governance initiatives",
      "Act as clinical lead or professional lead for rheumatology physiotherapy",
      "Provide formal supervision and act as practice educator",
      "Contribute to multidisciplinary working at senior level",
    ],
    developmentFocus: [
      "Pursue MSc or postgraduate qualification in Advanced Practice",
      "Complete HEE / NHSE APP framework alignment activities",
      "Develop leadership portfolio (NHS Leadership Academy, GMTS)",
      "Engage with clinical audit, QI, and service redesign",
      "Aim for or prepare for Band 8a Advanced Practitioner role",
    ],
    transition: "Band 7 is the gateway to Advanced Practitioner or Consultant Physiotherapist pathways. Structured APP portfolio development is key.",
  },
];

const COLOUR_MAP: Record<string, { header: string; border: string; bg: string }> = {
  emerald: { header: "from-emerald-700 to-emerald-900", border: "border-emerald-200", bg: "bg-emerald-50" },
  teal: { header: "from-teal-700 to-teal-900", border: "border-teal-200", bg: "bg-teal-50" },
  blue: { header: "from-blue-700 to-blue-900", border: "border-blue-200", bg: "bg-blue-50" },
};

export default function BandProgression() {
  return (
    <PhysioLayout title="Band Progression: 5 → 6 → 7">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <TrendingUp className="h-8 w-8 text-emerald-300" />
            <div>
              <h2 className="text-xl font-bold">NHS Band Progression Guide</h2>
              <p className="text-emerald-300 text-sm">Band 5 → 6 → 7 · Rheumatology Physiotherapy</p>
            </div>
          </div>
          <p className="text-emerald-200 text-sm leading-relaxed">
            Structured competency and development guidance for each Agenda for Change band in rheumatology physiotherapy. Use this alongside your appraisal and supervision to plan your progression.
          </p>
        </div>

        <div className="space-y-5">
          {BANDS.map((band) => {
            const c = COLOUR_MAP[band.colour];
            return (
              <div key={band.band} className={`rounded-2xl border ${c.border} overflow-hidden bg-white shadow-sm`}>
                <div className={`bg-gradient-to-r ${c.header} p-5 text-white`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs font-bold uppercase tracking-widest text-white/60">{band.band}</span>
                      <h3 className="text-lg font-bold mt-0.5">{band.title}</h3>
                    </div>
                    <span className="text-sm text-white/70">{band.duration}</span>
                  </div>
                </div>
                <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Key Competencies</h4>
                    <ul className="space-y-2">
                      {band.competencies.map((c) => (
                        <li key={c} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">Development Focus</h4>
                    <ul className="space-y-2">
                      {band.developmentFocus.map((d) => (
                        <li key={d} className="flex items-start gap-2 text-sm text-slate-700">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0 mt-2" />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div className={`${c.bg} border-t ${c.border} px-5 py-3`}>
                  <p className="text-xs text-slate-600 leading-relaxed"><strong>Progression note:</strong> {band.transition}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-5">
          <h3 className="font-bold text-slate-900 mb-2">Interview Preparation Tips</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-700">
            {[
              "Use STAR technique (Situation, Task, Action, Result) for competency questions",
              "Prepare examples of complex case management and clinical reasoning",
              "Demonstrate knowledge of current NICE guidelines and evidence base",
              "Show awareness of NHS Long Term Plan and MSK priorities",
              "Evidence service development or quality improvement involvement",
              "Highlight CPD activities and their impact on practice",
              "Show commitment to supervision and supporting others",
              "Research the trust and department you are applying to",
            ].map((tip) => (
              <div key={tip} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0 mt-2" />
                {tip}
              </div>
            ))}
          </div>
        </div>
      </div>
    </PhysioLayout>
  );
}
